import type { GetMetricsGated } from '@/common/api/models/getMetricsGated';

/** Shared metrics filter defaults (match server). */
export const METRICS_FILTER_DEFAULTS = {
  gated: 'gated' as GetMetricsGated,
  excludePasteHeavy: true,
  excludeFirstTryPasses: true,
};

let cachedDefaultMetricsDateRange: { from: string; to: string } | undefined;

function computeDefaultMetricsDateRange(): { from: string; to: string } {
  const to = new Date();
  const from = new Date();
  from.setUTCDate(from.getUTCDate() - 90);
  return {
    from: from.toISOString(),
    to: to.toISOString(),
  };
}

/**
 * Returns the default inclusive/exclusive ISO datetimes for the last 90 days.
 * Cached for the JS session so URL omit logic stays stable across a filter edit.
 */
export function getDefaultMetricsDateRange(): { from: string; to: string } {
  if (!cachedDefaultMetricsDateRange) {
    cachedDefaultMetricsDateRange = computeDefaultMetricsDateRange();
  }
  return cachedDefaultMetricsDateRange;
}

/** Test-only: clears the cached default date range. */
export function resetDefaultMetricsDateRangeCacheForTests(): void {
  cachedDefaultMetricsDateRange = undefined;
}

export type MetricsReportFilterKey =
  | 'gated'
  | 'from'
  | 'to'
  | 'excludePasteHeavy'
  | 'excludeFirstTryPasses';

export type MetricsReportFilters = {
  gated: GetMetricsGated;
  from: string;
  to: string;
  excludePasteHeavy: boolean;
  excludeFirstTryPasses: boolean;
};

/**
 * Builds the full filter object used for API requests (defaults + route overrides).
 */
export function resolveMetricsReportFilters(
  routeOverrides: Partial<MetricsReportFilters>,
): MetricsReportFilters {
  const range = getDefaultMetricsDateRange();
  return {
    gated: routeOverrides.gated ?? METRICS_FILTER_DEFAULTS.gated,
    from: routeOverrides.from ?? range.from,
    to: routeOverrides.to ?? range.to,
    excludePasteHeavy: routeOverrides.excludePasteHeavy ?? METRICS_FILTER_DEFAULTS.excludePasteHeavy,
    excludeFirstTryPasses:
      routeOverrides.excludeFirstTryPasses ?? METRICS_FILTER_DEFAULTS.excludeFirstTryPasses,
  };
}

/**
 * Route params that differ from defaults (for a clean URL).
 */
export function metricsFiltersToRouteParams(
  filters: MetricsReportFilters,
): Partial<MetricsReportFilters> {
  const range = getDefaultMetricsDateRange();
  const params: Partial<MetricsReportFilters> = {};
  if (filters.gated !== METRICS_FILTER_DEFAULTS.gated) {
    params.gated = filters.gated;
  }
  if (filters.from !== range.from) {
    params.from = filters.from;
  }
  if (filters.to !== range.to) {
    params.to = filters.to;
  }
  if (filters.excludePasteHeavy !== METRICS_FILTER_DEFAULTS.excludePasteHeavy) {
    params.excludePasteHeavy = filters.excludePasteHeavy;
  }
  if (filters.excludeFirstTryPasses !== METRICS_FILTER_DEFAULTS.excludeFirstTryPasses) {
    params.excludeFirstTryPasses = filters.excludeFirstTryPasses;
  }
  return params;
}

/** Route params for setParams after one filter changes (clears keys reset to default). */
export function routeParamsAfterFilterChange(
  applied: MetricsReportFilters,
  name: MetricsReportFilterKey,
  value: MetricsReportFilters[typeof name],
): Record<string, string | boolean | undefined> {
  const next = { ...applied, [name]: value };
  const params: Record<string, string | boolean | undefined> = {
    ...metricsFiltersToRouteParams(next),
  };
  if (!Object.prototype.hasOwnProperty.call(params, name)) {
    params[name] = undefined;
  }
  return params;
}

/**
 * Parses drawer route params into partial filter overrides.
 */
export function parseMetricsRouteParams(
  params: Record<string, unknown> | undefined,
): Partial<MetricsReportFilters> {
  if (!params) return {};
  const out: Partial<MetricsReportFilters> = {};
  if (params.gated === 'gated' || params.gated === 'ungated') {
    out.gated = params.gated;
  }
  if (typeof params.from === 'string' && params.from.length > 0) {
    out.from = params.from;
  }
  if (typeof params.to === 'string' && params.to.length > 0) {
    out.to = params.to;
  }
  if (params.excludePasteHeavy === true || params.excludePasteHeavy === 'true') {
    out.excludePasteHeavy = true;
  }
  if (params.excludePasteHeavy === false || params.excludePasteHeavy === 'false') {
    out.excludePasteHeavy = false;
  }
  if (params.excludeFirstTryPasses === true || params.excludeFirstTryPasses === 'true') {
    out.excludeFirstTryPasses = true;
  }
  if (params.excludeFirstTryPasses === false || params.excludeFirstTryPasses === 'false') {
    out.excludeFirstTryPasses = false;
  }
  return out;
}
